import { makeAutoObservable, runInAction } from 'mobx'

import { restClient } from '@/Core'

import type { TaskHistoryItem, TaskUserRef } from '../Contracts/Task.types'

/** Скільки подій тягнемо за раз: з запасом на найдовшу історію завдання. */
export const TASK_HISTORY_LIMIT = 100

/** Можливі форми відповіді `GET .../history` (бек віддає `{items}`, SPEC-003 §5.6). */
type HistoryResponse =
    | { items?: TaskHistoryItem[]; 'hydra:member'?: TaskHistoryItem[]; meta?: { total?: number } }
    | TaskHistoryItem[]
    | null

const extractItems = (data: HistoryResponse): TaskHistoryItem[] => {
    if (Array.isArray(data)) return data
    const items = data?.items ?? data?.['hydra:member']
    return Array.isArray(items) ? items : []
}

const extractList = <T,>(data: unknown): T[] => {
    if (Array.isArray(data)) return data as T[]
    const items = (data as { items?: unknown } | null)?.items
    return Array.isArray(items) ? (items as T[]) : []
}

const USER_KEYS = ['manager', 'responsibles', 'supervisors'] as const

/**
 * Людей у подіях бек пише ідентифікаторами (`responsibles`, `manager`,
 * `supervisors`, `added`) — збираємо їх, щоб показати іменами.
 */
const collectUserUuids = (items: TaskHistoryItem[]): string[] => {
    const uuids = new Set<string>()

    const take = (value: unknown): void => {
        if (typeof value === 'string' && value !== '') uuids.add(value)
        if (Array.isArray(value)) value.forEach(take)
    }

    for (const item of items) {
        for (const side of [item.payload?.old, item.payload?.new]) {
            for (const key of USER_KEYS) take(side?.[key])
        }
        take((item.payload as { added?: unknown } | undefined)?.added)
    }

    return [...uuids]
}

/**
 * Локальний стор `TaskHistoryTab` (SPEC-008 §5.2).
 *
 * Тримає події та все, що потрібно, щоб показати їх людською мовою: імена
 * людей, згаданих у змінах, і тексти коментарів. Інстанс — per-mount
 * (`useState(() => new TaskHistoryTabStore())`).
 */
export class TaskHistoryTabStore {
    history: TaskHistoryItem[] = []
    /** Скільки подій усього — якщо більше, ніж показано, стрічка про це скаже */
    total = 0
    users = new Map<string, TaskUserRef>()
    comments = new Map<string, string>()
    /** Коментарі завантажено: без цього «не знайдено» означало б «не встигли» */
    commentsLoaded = false
    loading = false

    constructor() {
        makeAutoObservable(this)
    }

    async load(uuid: string | undefined): Promise<void> {
        if (!uuid) return
        this.loading = true
        try {
            // Рантайм `restClient.get` повертає розпарсений JSON, але оголошений тип —
            // ky `ResponsePromise`; звужуємо через `unknown` (конвенція репо), без `any`.
            // forceRefresh: ядровий RestClient дедуплікує GET на 30с (sharedPromises) —
            // без нього повторне відкриття вкладки після Старт/Пауза показує стару історію.
            const raw = await restClient.get(`/services/tasks/${uuid}/history?limit=${TASK_HISTORY_LIMIT}`, undefined, {
                forceRefresh: true,
            })
            const data = raw as unknown as HistoryResponse
            const items = extractItems(data)
            const total = !Array.isArray(data) && typeof data?.meta?.total === 'number' ? data.meta.total : items.length

            // Імена й тексти — необов'язкові: без них стрічка все одно покаже події
            const [users, comments] = await Promise.all([
                this.fetchUsers(collectUserUuids(items)),
                items.some((item) => item.event === 'comment_added') ? this.fetchComments(uuid) : Promise.resolve(null),
            ])

            runInAction(() => {
                this.history = items
                this.total = total
                this.users = users
                this.comments = comments ?? new Map()
                this.commentsLoaded = comments !== null
            })
        } catch {
            runInAction(() => {
                this.history = []
                this.total = 0
            })
        } finally {
            runInAction(() => {
                this.loading = false
            })
        }
    }

    private async fetchUsers(uuids: string[]): Promise<Map<string, TaskUserRef>> {
        const users = new Map<string, TaskUserRef>()
        if (uuids.length === 0) return users

        const filters = JSON.stringify({ and: [{ field: 'uuid', op: 'in', value: uuids }] })

        try {
            const raw = await restClient.get(
                `/services/users?limit=${uuids.length}&filters=${encodeURIComponent(filters)}`
            )
            for (const user of extractList<TaskUserRef>(raw as unknown)) {
                if (user.uuid) users.set(user.uuid, user)
            }
        } catch {
            // Без імен стрічка напише «невідомий користувач» — не привід її ховати
        }

        return users
    }

    private async fetchComments(uuid: string): Promise<Map<string, string> | null> {
        try {
            const raw = await restClient.get(`/services/tasks/${uuid}/comments?limit=${TASK_HISTORY_LIMIT}`, undefined, {
                forceRefresh: true,
            })
            const comments = new Map<string, string>()
            for (const comment of extractList<{ uuid?: string; text?: string }>(raw as unknown)) {
                if (comment.uuid) comments.set(comment.uuid, comment.text ?? '')
            }
            return comments
        } catch {
            return null
        }
    }
}
